import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Input,
  model,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
} from '@angular/core';
import {
  BottomSheetComponent,
  DeviceService,
  TabsInkbarFixDirective,
} from '@nomoh-monorepo/shared-common-ui';
import {
  IAgendamento,
  IEstabelecimentoClienteEntity,
} from '@nomoh-monorepo/shared-types';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { SkeletonModule } from 'primeng/skeleton';
import { TabsModule } from 'primeng/tabs';
import {
  NichoTermosService,
  SetupOperacionalGateService,
  SetupOperacionalOrigem,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AgendaListaClienteAgendamentoComponent } from './agenda-clientes/agenda-lista-cliente.component';
import { AgendaNovoAgendamentoComponent } from './agenda-novo-agendamento/agenda-novo-agendamento.component';

@Component({
  selector: 'lib-agenda-modal-novo-agendamento',
  standalone: true,
  imports: [
    CommonModule,
    AgendaNovoAgendamentoComponent,
    AgendaListaClienteAgendamentoComponent,

    DialogModule,
    BottomSheetComponent,
    SkeletonModule,
    TabsModule,
    TabsInkbarFixDirective,
    ButtonModule,
    TranslateModule,
  ],
  templateUrl: './agenda-modal-novo-agendamento.component.html',
  styleUrl: './agenda-modal-novo-agendamento.component.scss',
  providers: [DialogService],
})
export class AgendaModalNovoAgendamentoComponent {
  //#region Injeção de Dependências
  deviceService = inject(DeviceService);
  private translateService = inject(TranslateService);
  private setupOperacionalGate = inject(SetupOperacionalGateService);
  $nicho = inject(NichoTermosService).$nicho;
  //#endregion
  //#region ViewChild
  @ViewChild(AgendaListaClienteAgendamentoComponent)
  listaClienteRef?: AgendaListaClienteAgendamentoComponent;
  //#endregion
  //#region Inputs
  @Input() agendamento: IAgendamento = {} as IAgendamento;

  /**
   * Quando `true`, o modal abre no modo Encaixe (agendamento extraordinário,
   * ignora validação de disponibilidade/conflito de horário no backend).
   */
  @Input() isEncaixe = false;
  //#endregion

  //#region signals
  $modalIsVisible = model(false);
  $isLoading = signal(false);
  $activeTab = signal<string>('clientes');
  $modoClientes = signal<'lista' | 'criar'>('lista');
  private $conteudoLiberado = signal(false);
  private $listaPronta = signal(false);
  private preparacaoSeq = 0;
  $mostrarSkeleton = computed(
    () => !this.$conteudoLiberado() || !this.$listaPronta()
  );
  readonly skeletonCards = [1, 2, 3, 4];
  //#endregion

  constructor() {
    effect(() => {
      const visible = this.$modalIsVisible();
      if (!visible) {
        this.preparacaoSeq++;
        untracked(() => this.resetarConteudo());
        return;
      }

      untracked(() => {
        void this.liberarConteudo();
      });
    });
  }

  //#region propriedades
  headerTemplate: TemplateRef<unknown> | null = null;
  footerTemplate: TemplateRef<unknown> | null = null;
  footerClienteRef: TemplateRef<unknown> | null = null;
  cliente: IEstabelecimentoClienteEntity = {} as IEstabelecimentoClienteEntity;

  $tabs = computed(() => [
    { id: 'clientes', label: this.$nicho().cliente.plural, icon: 'pi pi-pencil' },
    {
      id: 'agendar',
      label: this.translateService.instant('BARDO_AGENDA.AGENDAR') || 'Agendar',
      icon: 'pi pi-eye',
    },
  ]);


  //#endregion
  onModalVisibleChange(visible: boolean): void {
    this.$modalIsVisible.set(visible);
    if (!visible) {
      this.limparEstadoModal();
    }
  }

  onListaPronta(): void {
    if (!this.$modalIsVisible()) {
      return;
    }
    this.$listaPronta.set(true);
  }

  fecharModal(): void {
    this.$modalIsVisible.set(false);
    this.limparEstadoModal();
  }

  private limparEstadoModal(): void {
    this.footerTemplate = null;
    this.headerTemplate = null;
    this.footerClienteRef = null;
    this.cliente = {} as IEstabelecimentoClienteEntity;
    this.$activeTab.set('clientes');
    this.$modoClientes.set('lista');
    this.resetarConteudo();
  }

  private resetarConteudo(): void {
    this.$conteudoLiberado.set(false);
    this.$listaPronta.set(false);
  }

  private async liberarConteudo(): Promise<void> {
    const seq = ++this.preparacaoSeq;
    const resultado = await this.setupOperacionalGate.validar();
    if (seq !== this.preparacaoSeq || !this.$modalIsVisible()) {
      return;
    }

    if (!resultado.ok) {
      const origem: SetupOperacionalOrigem = this.isEncaixe
        ? 'agenda-encaixe'
        : 'agenda';
      const ok = await this.setupOperacionalGate.solicitarConclusao(origem);
      if (seq !== this.preparacaoSeq || !this.$modalIsVisible()) {
        return;
      }
      if (!ok) {
        this.fecharModal();
        return;
      }
    }

    this.$conteudoLiberado.set(true);
  }

  headerTemplateAction(template: TemplateRef<unknown>): void {
    this.headerTemplate = template;
  }

  footerTemplateAction(template: TemplateRef<unknown>): void {
    this.footerTemplate = template;
  }

  tabVisivel(tabId: string): boolean {
    if (tabId === 'agendar') {
      return !!this.cliente?.id;
    }
    return true;
  }

  onTabChange(tabId: string) {
    this.$activeTab.set(tabId);
    this.$modoClientes.set('lista');
    this.footerClienteRef = null;
    this.listaClienteRef?.cancelarCriacao();
  }

  onModoClientesChanged(modo: 'lista' | 'criar'): void {
    this.$modoClientes.set(modo);
    if (modo === 'lista') {
      this.footerClienteRef = null;
    }
  }

  onFooterClienteTemplate(template: TemplateRef<unknown>): void {
    this.footerClienteRef = template;
  }

  salvarNovoCliente(): void {
    this.listaClienteRef?.salvarCliente();
  }

  cancelarNovoCliente(): void {
    this.listaClienteRef?.cancelarCriacao();
  }

  clienteSelecionado(cliente: IEstabelecimentoClienteEntity) {
    this.cliente = cliente;
    this.$activeTab.set('agendar');
  }
}
